import type { PartialBlock } from "@blocknote/core";
import { inlineContentToText } from "@/lib/utils/blocknote-plain-text";

export type AiNotesBullet = {
  text: string;
  sub?: AiNotesBullet[];
};

export type AiNotesSection = {
  title: string;
  bullets?: AiNotesBullet[];
};

export function aiNotesToBlocks(notes: AiNotesSection[]): PartialBlock[] {
  const blocks: PartialBlock[] = [];

  const addBullets = (bullets: AiNotesBullet[]) => {
    bullets.forEach((bullet) => {
      blocks.push({
        type: "bulletListItem",
        content: [{ type: "text" as const, text: String(bullet.text ?? ""), styles: {} }],
      });

      // BlockNote doesn't support nested bullets directly — emit as indented siblings.
      if (bullet.sub && bullet.sub.length > 0) {
        bullet.sub.forEach((subBullet) => {
          blocks.push({
            type: "bulletListItem",
            content: [{ type: "text" as const, text: `  ${String(subBullet.text ?? "")}`, styles: {} }],
          });

          if (subBullet.sub && subBullet.sub.length > 0) {
            subBullet.sub.forEach((subSubBullet) => {
              blocks.push({
                type: "bulletListItem",
                content: [{ type: "text" as const, text: `    ${String(subSubBullet.text ?? "")}`, styles: {} }],
              });
            });
          }
        });
      }
    });
  };

  notes.forEach((section) => {
    if (!section || !section.title) return;

    blocks.push({
      type: "heading",
      props: { level: 2 },
      content: [{ type: "text" as const, text: section.title, styles: {} }],
    });

    if (section.bullets && Array.isArray(section.bullets)) {
      addBullets(section.bullets);
    }

    blocks.push({ type: "paragraph" });
  });

  return blocks.length > 0 ? blocks : [{ type: "paragraph" }];
}

const MAX_AI_NOTES_DEPTH = 2; // top-level bullet → sub → sub-sub, as aiNotesToBlocks emits

type BlockLike = {
  type?: string;
  content?: unknown;
  children?: BlockLike[];
};

/**
 * Inverse of `aiNotesToBlocks`: turn the (possibly user-edited) editor
 * document back into the `aiNotesContent` section shape.
 *
 * - A heading starts a new section titled with its text. An empty heading
 *   doesn't start a section (an untitled section would be dropped on reload).
 * - Any other text block (bullet, numbered/check item, paragraph) becomes a
 *   bullet. Empty blocks are dropped.
 * - Nesting comes from the 2/4-space indentation `aiNotesToBlocks` emits for
 *   sub/sub-sub bullets, plus BlockNote children (Tab-indented items). Depth is
 *   capped at sub-sub, and an item with no parent at the level above is
 *   promoted rather than dropped.
 * - Content before the first heading goes into a section titled
 *   `leadingTitle`, so it survives (aiNotesToBlocks skips untitled sections).
 */
export function blocksToAiNotesSections(
  blocks: readonly BlockLike[],
  leadingTitle = "Notes",
): AiNotesSection[] {
  const sections: AiNotesSection[] = [];
  let current: AiNotesSection | null = null;
  // Last bullet seen at each depth, to attach deeper bullets to.
  let lastAtDepth: AiNotesBullet[] = [];

  const ensureSection = (): AiNotesSection => {
    if (!current) {
      current = { title: leadingTitle, bullets: [] };
      sections.push(current);
      lastAtDepth = [];
    }
    return current;
  };

  const addBullet = (rawText: string, childDepth: number) => {
    const indent = rawText.match(/^ */)?.[0].length ?? 0;
    const text = rawText.trim();
    if (!text) return;
    const indentDepth = indent >= 4 ? 2 : indent >= 2 ? 1 : 0;
    let depth = Math.min(childDepth + indentDepth, MAX_AI_NOTES_DEPTH);
    // Promote when there's no parent to nest under.
    while (depth > 0 && !lastAtDepth[depth - 1]) depth -= 1;

    const section = ensureSection();
    const bullet: AiNotesBullet = { text };
    if (depth === 0) {
      section.bullets!.push(bullet);
    } else {
      const parent = lastAtDepth[depth - 1];
      (parent.sub ??= []).push(bullet);
    }
    lastAtDepth = [...lastAtDepth.slice(0, depth), bullet];
  };

  const walk = (list: readonly BlockLike[], childDepth: number) => {
    for (const block of list) {
      if (!block) continue;
      const text = inlineContentToText(block.content);
      if (block.type === "heading" && childDepth === 0) {
        const title = text.trim();
        if (title) {
          current = { title, bullets: [] };
          sections.push(current);
          lastAtDepth = [];
        }
      } else {
        addBullet(text, childDepth);
      }
      if (Array.isArray(block.children) && block.children.length > 0) {
        walk(block.children, childDepth + 1);
      }
    }
  };

  walk(blocks, 0);
  return sections;
}
